import { injectIntl, IntlShape } from 'react-intl';
import { get, isEmpty } from 'lodash';
import { Grid } from '@material-ui/core';
import { memo } from 'react';
import { translate } from 'containers/services/translate';
import messages from 'containers/CourtManagerLegalProceedings/Provision/Regulation/messages';
import { Listings, SagisConfig } from 'containers/AppContainer/typings';
import {
  Title,
  Field,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/styled-components';
import {
  formatDate,
  getCityByCode,
  getPaymentNature,
  getProcessNature,
  getRegulationNatureTitle,
  getCityCodeByRisk,
  getPaymentMethod,
  getPaymentChannel,
  getOppositLawyerCityByCode,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/utils';
import { REGULATIONS } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/data-structure';
import {
  RECIPIENT,
  REGULATION_LEVEL_REQUIREMENT,
  REGULATION_STATUS,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import { RcCorporeal } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/typings';
import SahamAccordion from 'components/SahamAccordion';
import {
  EXPAND_RISK,
  IDD_CHECK,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/Recap/constants';

export interface RegulationInformationRecapProps {
  intl: IntlShape;
  listings: Listings;
  rcCorporeal: RcCorporeal;
  corpoEncryptedDocuments: any;
  substituteDocumentNature: SagisConfig[];
  corpoEncryptedDocumentsResult: any;
  expanded: any;
  handleChangeExpanded: Function;
  isBasicManagementRegulation?: boolean;
}

export const RegulationInformationRecap: React.FC<RegulationInformationRecapProps> = memo(
  ({
    rcCorporeal,
    listings,
    corpoEncryptedDocuments,
    substituteDocumentNature,
    corpoEncryptedDocumentsResult,
    ...props
  }): JSX.Element => {
    return (
      <main>
        <SahamAccordion
          key={rcCorporeal.regulationId}
          title={translate(messages.judgementInformation, props)}
          expanded={get(props.expanded[rcCorporeal.regulationId], [
            EXPAND_RISK.INFO,
          ])}
          onChange={props.handleChangeExpanded(rcCorporeal, [EXPAND_RISK.INFO])}
        >
          <Grid container spacing={1} direction="row">
            {!props.isBasicManagementRegulation && (
              <Grid item xs={4}>
                <Title> {translate(messages.processNature, props)}</Title>
                <Field>{getProcessNature(listings, rcCorporeal)}</Field>
              </Grid>
            )}
            <Grid item xs={4}>
              <Title> {translate(messages.regulationNature, props)}</Title>
              <Field>
                {getRegulationNatureTitle(
                  substituteDocumentNature,
                  rcCorporeal,
                )}
              </Field>
            </Grid>
            {!isEmpty(
              get(rcCorporeal.regulationInfo, [REGULATIONS.TPI], ''),
            ) && (
              <Grid item xs={4}>
                <Title>{translate({ ...messages.tpi }, props)}</Title>
                <Field>
                  {get(rcCorporeal.regulationInfo, [REGULATIONS.TPI], '')}
                </Field>
              </Grid>
            )}
            {!isEmpty(
              get(rcCorporeal.regulationInfo, [REGULATIONS.APPEAL_COURTS], ''),
            ) && (
              <Grid item xs={4}>
                <Title>{translate({ ...messages.appealCourt }, props)}</Title>
                <Field>
                  {get(
                    rcCorporeal.regulationInfo,
                    [REGULATIONS.APPEAL_COURTS],
                    '',
                  )}
                </Field>
              </Grid>
            )}
            <Grid item xs={4}>
              <Title>{translate(messages.regulationPhase, props)}</Title>
              <Field>
                {get(
                  getPaymentNature(get(listings, 'PaymentNature', [])),
                  'ItemDescription',
                  '',
                )}
              </Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.paymentMethod, props)}</Title>
              <Field>{getPaymentMethod(rcCorporeal, listings)}</Field>
            </Grid>

            {(get(rcCorporeal, 'regulationInfo.beneficiaryRecipient', '') ===
              RECIPIENT.BNEJ ||
              (get(rcCorporeal, 'regulationInfo.isRecap', false) &&
                get(rcCorporeal, 'regulationInfo.isBnej', false))) && (
              <Grid item xs={4}>
                <Title> {translate(messages.bnejCity, props)}</Title>
                <Field>{getCityCodeByRisk(rcCorporeal, listings)}</Field>
              </Grid>
            )}
            {(get(rcCorporeal, 'regulationInfo.beneficiaryRecipient', '') ===
              RECIPIENT.BNEJ ||
              (get(rcCorporeal, 'regulationInfo.isRecap', false) &&
                get(rcCorporeal, 'regulationInfo.isBnej', false))) && (
              <Grid item xs={4}>
                <Title>{translate(messages.bnejNumberTitle, props)}</Title>
                <Field>
                  {get(rcCorporeal, 'regulationInfo.BNEJNumber', '')}
                </Field>
              </Grid>
            )}

            <Grid item xs={4}>
              <Title>{translate(messages.beneficiaryReference, props)}</Title>
              <Field>
                {get(
                  rcCorporeal.regulationInfo,
                  [REGULATIONS.BENEFICIARY_REFERENCE],
                  '',
                )}
              </Field>
            </Grid>
            {get(rcCorporeal, 'regulationInfo.beneficiaryRecipient', '') !==
              RECIPIENT.CDRA && (
              <>
                <Grid item xs={4}>
                  <Title>{translate(messages.town, props)}</Title>
                  <Field>{getCityByCode(rcCorporeal, listings)}</Field>
                </Grid>
              </>
            )}
            <Grid item xs={4}>
              <Title>{translate(messages.opposingLawyer, props)}</Title>
              <Field>
                {get(
                  rcCorporeal.regulationInfo,
                  [REGULATIONS.OPPOSING_LAWYER],
                  '',
                )}
              </Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.opposingLawyerCity, props)}</Title>
              <Field>{getOppositLawyerCityByCode(rcCorporeal, listings)}</Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.opposingLawyerAddress, props)}</Title>
              <Field>
                {get(
                  rcCorporeal.regulationInfo,
                  [REGULATIONS.OPPOSING_LAWYER_ADDRESS],
                  '',
                )}
              </Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.opposingLawyerEmail, props)}</Title>
              <Field>
                {get(
                  rcCorporeal.regulationInfo,
                  [REGULATIONS.OPPOSING_LAWYER_EMAIL],
                  '',
                )}
              </Field>
            </Grid>
            {!isEmpty(
              get(
                rcCorporeal.regulationInfo,
                [REGULATIONS.JUDGEMENT_DATE_JAF],
                '',
              ),
            ) && (
              <>
                {get(
                  rcCorporeal.regulationInfo,
                  [REGULATIONS.IS_APPEAL],
                  '',
                ) && (
                  <Grid item xs={4}>
                    <Title>{translate(messages.appealDate, props)}</Title>
                    <Field>
                      {formatDate(
                        get(
                          rcCorporeal.regulationInfo,
                          [REGULATIONS.JUDGEMENT_DATE],
                          null,
                        ),
                      )}
                    </Field>
                  </Grid>
                )}
                <Grid item xs={4}>
                  <Title>{translate(messages.judgementDate, props)}</Title>
                  <Field>
                    {formatDate(
                      get(
                        rcCorporeal.regulationInfo,
                        [REGULATIONS.JUDGEMENT_DATE_JAF],
                        null,
                      ),
                    )}
                  </Field>
                </Grid>
              </>
            )}
            {isEmpty(
              get(
                rcCorporeal.regulationInfo,
                [REGULATIONS.JUDGEMENT_DATE_JAF],
                '',
              ),
            ) && (
              <Grid item xs={4}>
                <Title>
                  {get(rcCorporeal.regulationInfo, [REGULATIONS.IS_APPEAL], '')
                    ? translate(messages.appealDate, props)
                    : translate(messages.judgementDate, props)}
                </Title>
                <Field>
                  {formatDate(
                    get(
                      rcCorporeal.regulationInfo,
                      [REGULATIONS.JUDGEMENT_DATE],
                      null,
                    ),
                  )}
                </Field>
              </Grid>
            )}
            {(get(
              rcCorporeal.regulationInfo,
              [REGULATIONS.BENEFICIARY_RECIPIENT],
              '',
            ) === RECIPIENT.BNEJ ||
              (get(rcCorporeal, 'regulationInfo.isRecap', false) &&
                get(rcCorporeal, 'regulationInfo.isBnej', false))) && (
              <Grid item xs={4}>
                <Title>{translate(messages.taxAmount, props)}</Title>
                <Field>
                  {get(
                    rcCorporeal.regulationInfo,
                    [REGULATIONS.BNEJ_TAX_AMOUNT],
                    '',
                  )}
                </Field>
              </Grid>
            )}
            <Grid item xs={4}>
              <Title>{translate(messages.regulationDate, props)}</Title>
              <Field>
                {get(
                  rcCorporeal.regulationInfo,
                  [REGULATIONS.REGULATION_DATE],
                  null,
                )}
              </Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.paymentChannel, props)}</Title>
              <Field>{getPaymentChannel(listings, rcCorporeal)}</Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.costs, props)}</Title>
              <Field>
                {get(
                  rcCorporeal.regulationInfo,
                  [REGULATIONS.DIRECT_INDEMNITY_COSTS],
                  '',
                )}
              </Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.iddField, props)}</Title>
              <Field>
                {get(
                  rcCorporeal.regulationInfo,
                  [REGULATIONS.IS_IDD_CHECKED],
                  '',
                )}
              </Field>
            </Grid>
            {get(rcCorporeal.regulationInfo, [REGULATIONS.IS_IDD_CHECKED]) ===
              IDD_CHECK.TRUE && (
              <>
                {get(
                  rcCorporeal.regulationInfo,
                  [REGULATIONS.CALCULATION_START_DATE],
                  '',
                ) && (
                  <Grid item xs={4}>
                    <Title>{translate(messages.iddDate, props)}</Title>
                    <Field>
                      {formatDate(
                        get(
                          rcCorporeal.regulationInfo,
                          [REGULATIONS.CALCULATION_START_DATE],
                          '',
                        ),
                      )}
                    </Field>
                  </Grid>
                )}
                <Grid item xs={4}>
                  <Title>{translate(messages.iddDeduction, props)}</Title>
                  <Field>
                    {`${get(
                      rcCorporeal.regulationInfo,
                      [REGULATIONS.DEDUCTION_RATE],
                      '',
                    )} %`}
                  </Field>
                </Grid>
              </>
            )}
            <Grid item xs={4}>
              <Title>{translate(messages.status, props)}</Title>
              <Field>{REGULATION_STATUS}</Field>
            </Grid>
            <Grid item xs={4}>
              <Title>{translate(messages.levelRequirement, props)}</Title>
              <Field>{REGULATION_LEVEL_REQUIREMENT}</Field>
            </Grid>
          </Grid>
        </SahamAccordion>
      </main>
    );
  },
);
export default injectIntl(RegulationInformationRecap);
